<script setup lang="ts">
//#region imports
import { computed, ref, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';
import UiPagination from '@/components/UIElements/UiPagination.vue';
import { useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import { usePopupManager } from '@/components/PopupManager.vue';
import { locales } from '@/helpers/date-locales';
import { useLs } from '@/setup/vueLs';
import { useApi } from '@/api';
import { useTypedStore } from '@/store';
import { useTeamsList } from '@/composables/useTeamsList';
import { useFormatters } from '@/composables/useFormatters';
import updateUrl from '@/service/updateUrl';
import _ from '@/setup/lodash';
import { useFormatCurrency } from '@/composables/useFormatCurrency';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type {
  GetMediaBuyingPaymentsData,
  GetMediaBuyingPaymentsResponse,
} from '@/types/openapi-ts';
import type {
  MediaBuyingModerationStatusSections,
  MediaBuyingModerationStatus,
  MediaBuyingPayoutStatus,
} from '@/types/status';
import type { PermissionGroupType } from '@/composables/useDetectPermissions';
//#endregion

//#region types
type MediaBuyingPaymentRow = GetMediaBuyingPaymentsResponse['payload'][number];
type MediaBuyingPaymentPermissions = NonNullable<GetMediaBuyingPaymentsResponse['misc']>['permissions'];
type MediaBuyingQuery = NonNullable<GetMediaBuyingPaymentsData['query']>;
//#endregion

//#region const
const sizes = {
  1080: 20,
  1440: 30,
};

const { renderNeedAttentionIcon } = useTeamsList<MediaBuyingPaymentRow>();
const fields: UiTableNewField<MediaBuyingPaymentRow>[] = [
  {
    name: '',
    fixed: 'left',
    align: 'right',
    sortable: false,
    width: '30',
    minWidth: '30',
    linkAppend: renderNeedAttentionIcon,
  },
  {
    name: 'incrementing_id',
    fixed: 'left',
    align: 'left',
    sortable: 'custom',
    width: '60',
    minWidth: '60',
  },
  {
    name: 'affiliate_email',
    align: 'left',
    sortable: false,
    width: '180',
    minWidth: '180',
    link: getAffiliateLink,
  },
  {
    name: 'team_name',
    width: '120',
    minWidth: '120',
  },
  {
    name: 'campaign_name',
    align: 'left',
    sortable: false,
    minWidth: '180',
    flexible: true,
  },
  {
    name: 'amount',
    align: 'right',
    width: '120',
    minWidth: '120',
    sortable: false,
    computedValue: (row: MediaBuyingPaymentRow) => formatCurrencyValue(row.currency_code, row.amount),

  },
  {
    name: 'amount_converted',
    align: 'right',
    width: '120',
    minWidth: '120',
    sortable: 'custom',
    format: 'formatMoney',
    computedValue: (row: MediaBuyingPaymentRow) => formatCurrencyValue(currencyCode.value, row.amount_converted),
  },
  {
    name: 'payment_method_id',
    align: 'center',
    className: 'payment-center',
    headerAlign: 'center',
    width: '120',
    minWidth: '120',
    computedImg: computedPaymentMethodImg,
    computedImgNull: computedPaymentMethod,
  },
  {
    name: 'period',
    align: 'left',
    width: '250',
    minWidth: '250',
    computedValue: computedPeriod,
  },
  {
    name: 'created_at',
    width: '140',
    minWidth: '140',
    align: 'left',
    headerAlign: 'left',
    sortable: 'custom',
    format: 'date-time',
  },
  {
    name: 'updated_at',
    width: '140',
    minWidth: '140',
    align: 'left',
    headerAlign: 'left',
    sortable: 'custom',
    format: 'date-time',
  },
];
//#endregion

//#region composables
const api = useApi();
const popupManager = usePopupManager();
const ls = useLs();
const store = useTypedStore();
const route = useRoute();
const { t, locale } = useI18n();
const { formatCurrencyValue } = useFormatters();
const { checkCurrencyCode } = useFormatCurrency();
const { checkRequest, checkRoute } = useDetectPermissions();

const { limit, count, page, setPage, setLsPathPage } = useTablePaginator({
  lsPath: getLsPath(),
  sizes,
  defaultSize: 15,
});

const { sortProp, sortOrder, setSort, optionsSort, setLsPath } = useTableSort({
  initialOptions:{
    sort_column: 'incrementing_id',
    sort_dir: 'desc',
  },
  lsPath: getLsPath(),
  fields,
});

const { searchQuery, setSearch } = useTableSearch();
//#endregion

//#region state
const needsAttentionFirst = ref(true);
const currentBuyingPageName = ref<PermissionGroupType>('' as PermissionGroupType);
const currentBuyingPageStatus = ref('');
const currencyCode = ref('');
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const tableData = ref<GetMediaBuyingPaymentsResponse['payload']>([]);
const permissions = ref<MediaBuyingPaymentPermissions>({});
//#endregion

//#region computed
const adminAcl = computed(() => store.getters['auth/adminAcl']);
const currentAcl = computed(() => store.getters['auth/currentAcl']);
const customerCurrencies = computed(() => store.getters['misc/customerCurrencies']);
const lsPath = computed(() => getLsPath());
//#endregion

//#region methods
const _query = () => {
  let result = {
    ...optionsSort.value,
    search: searchQuery.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
    convert_amount_to_currency_code: currencyCode.value,
    needs_attention_first: needsAttentionFirst.value,
  } as MediaBuyingQuery;

  if (currentBuyingPageName.value === 'media_buying_payments_moderation') {
    result.moderation_status = currentBuyingPageStatus.value as MediaBuyingQuery['moderation_status'];
  } else {
    result.moderation_status = 'approved';
    result.payout_status = currentBuyingPageStatus.value as MediaBuyingQuery['payout_status'];
  }

  return result;
};

const _completedFilterUrl = () => {
  const _dataFilters = {
    ..._.omit(_query(), ['offset', 'convert_amount_to_currency_code']),
    currency_code: currencyCode.value,
  };
  updateUrl.updateGetParams(_dataFilters);
};

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  let options = _query();

  try {
    const { data: { payload, misc } } = await api.getMediaBuyingPayments(options);

    count.value = misc!.count;
    permissions.value = misc!.permissions;
    currencyCode.value = misc!.currency_code;
    tableData.value = payload;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }
}

function initData() {
  currentBuyingPageName.value = route.meta.group as PermissionGroupType;
  currentBuyingPageStatus.value = route.meta.view as string;
  if (checkRequest('media_buying_payments_view', currentBuyingPageName.value, currentBuyingPageStatus.value)){
    const _urlData = updateUrl.getParseParams(route.hash);

    if (!_.isEmpty(_urlData)) {
      setPage(_urlData);

      delete _urlData.page;
      delete _urlData.limit;

      _.merge(optionsSort.value, _urlData);

      setSort(_urlData);
      setSearch(_urlData);
      currencyCode.value = checkCurrencyCode(_.get(_urlData, ['currency_code']) as string);
      needsAttentionFirst.value = _.get(_urlData, ['needs_attention_first']) === 'true';
    } else {
      currencyCode.value = checkCurrencyCode(ls.get(`${lsPath.value}/currency_code`));
      needsAttentionFirst.value = ls.get(`${lsPath.value}/needs_attention_first`) || true;
    }

    nextTick(() => {
      fetchData();
    });
  }
  checkRoute('media_buying_payments_view', currentBuyingPageName.value, currentBuyingPageStatus.value);
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

const handleRefresh = () => {
  page.value = 1;
  fetchData();
};

function changeCurrency(value: string) {
  currencyCode.value = value;
  ls.set(`${lsPath.value}/currency_code`, value);
  fetchData();
}
function changeAttention(value: boolean) {
  needsAttentionFirst.value = value;
  ls.set(`${lsPath.value}/needs_attention_first`, value);
  fetchData();
}

function getAffiliateLink(row: MediaBuyingPaymentRow) {
  const isUser = !adminAcl.value.is_superuser && permissions.value[row.id]!.view_profile;
  const isSuperUser = adminAcl.value.is_superuser;
  return isUser || isSuperUser ? `/affiliates/${row.affiliate_id}/info` : '';
}

function formatDate(value: string) {
  return format(value, 'dd MMM yyyy', { locale: locales[locale.value as keyof typeof locales] });
}

function computedPeriod(row: MediaBuyingPaymentRow) {
  return `${formatDate(row.payment_period_start)} - ${formatDate(row.payment_period_end)}`;
}

function computedPaymentMethodImg(row: MediaBuyingPaymentRow) {
  return row.payment_method_logo_base64!;
}

function computedPaymentMethod(value: any, row: MediaBuyingPaymentRow) {
  return row.payment_method_name;
}

function calcWidth() {
  const page = currentBuyingPageStatus.value === 'on_review' || currentBuyingPageStatus.value === 'processing';
  const isUser = !adminAcl.value.is_superuser && page && currentAcl.value.media_buying_payments_review === 'allow';
  const isSuperUser = adminAcl.value.is_superuser && page;
  return isUser || isSuperUser ? 105 : 76;
}

function reviewActions(row: MediaBuyingPaymentRow) {
  const isUser = !adminAcl.value.is_superuser && permissions.value && permissions.value[row.id]!.review_actions;
  const isSuperUser = adminAcl.value.is_superuser;
  return isSuperUser || isUser;
}

async function changeStatus(
  section: MediaBuyingModerationStatusSections,
  status: MediaBuyingModerationStatus |  MediaBuyingPayoutStatus,
  paymentId: string,
) {
  await api.postMediaBuyingPaymentChangeStatus(paymentId, section, status);
  await fetchData();
}

function handleRowClick(row: MediaBuyingPaymentRow) {
  return `/media-buying/payments/${row.id}?from=${currentBuyingPageName.value}&needs=success`;
}

function failedHandler(row: MediaBuyingPaymentRow) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      message: t('mediaBuying.info.popup.failed_request.text'),
      title: t('mediaBuying.info.popup.failed_request.title'),
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('mediaBuying.info.popup.failed_request.button'),
      confirmType: 'negative',
      confirmIcon: 'check',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        await changeStatus('payout', 'failed', row.id);
        context.handleClose();
      },
    },
  });
}

function declineHandler(row: MediaBuyingPaymentRow) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      message: t('mediaBuying.info.popup.decline_request.text'),
      title: t('mediaBuying.info.popup.decline_request.title'),
      confirmTitle: t('mediaBuying.info.popup.decline_request.button'),
      bodyIcon: 'ban',
      bodyIconColor: 'var(--text-negative)',
      confirmType: 'negative',
      confirmIcon: 'ban',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        await changeStatus('moderation', 'decline', row.id);
        context.handleClose();
      },
    },
  });
}

async function markAsRead(row: GetMediaBuyingPaymentsResponse['payload'][number]) {
  if (row.needs_attention) {
    await api.postMediaBuyingPaymentMarkAsRead(row.id);
  } else {
    await api.postMediaBuyingPaymentMarkAsUnread(row.id);
  }
  fetchData(true);
}

function getLsPath() {
  return `MediaBuying/Payments/${route.meta.group}/${route.meta.view}`;
}
//#endregion

//#region watch
watch(lsPath, (value) => {
  currentBuyingPageName.value = route.meta.group as PermissionGroupType;
  currentBuyingPageStatus.value = route.meta.view as string;

  setLsPath(value);
  setLsPathPage(value);

  searchQuery.value = '';
  currencyCode.value = checkCurrencyCode(ls.get(`${lsPath.value}/currency_code`));
  needsAttentionFirst.value = ls.get(`${lsPath.value}/needs_attention_first`) ?? true;

  nextTick(() => {
    fetchData();
  });
  popupManager.$scopeDispose();
});
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="media-buying-payments-list">
    <UiToolbar>
      <template v-slot:title>
        <div class="t:flex t:items-center t:gap-8">
          {{ $t(`mediaBuying.page_title.${currentBuyingPageName}.name`) }}
          <UiSeparator
            type="dot"
          />
          <UiStatusText
            :type="currentBuyingPageStatus"
            :text="$t(`mediaBuying.page_title.${currentBuyingPageName}.${currentBuyingPageStatus}`)"
            :font-size="20"
          />
        </div>
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
        <UiCheckbox
          v-model="needsAttentionFirst"
          :label="$t('mediaBuying.other.action_required')"
          @change="changeAttention"
        />
      </template>
      <template v-slot:right-slot>
        <UiCurrencyPicker
          :model-value="currencyCode"
          :currencies="customerCurrencies"
          @update:model-value="changeCurrency"
        />
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :currency="currencyCode"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="mediaBuying.list_table_title"
      @sort="handleSort"
    >
      <template v-slot:append>
        <ElTableColumn
          :width="calcWidth()"
          :resizable="false"
          fixed="right"
          align="center"
          header-align="center"
        >
          <template v-slot:header="scope">
            <div
              :key="scope.$index"
              class="table-action-header"
            >
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              :tooltip="scope.row.needs_attention
                ? $t('mediaBuying.other.btn_unmark')
                : $t('mediaBuying.other.btn_mark')"
              :icon="scope.row.needs_attention
                ? 'circle-exclamation-check'
                : 'exclamation-circle'"
              :color="scope.row.needs_attention
                ? 'var(--text-primary)'
                : 'var(--text-negative)'"
              @click="markAsRead(scope.row)"
            />
            <ActionIcon
              icon="eye"
              :tooltip="$t('mediaBuying.actions.info')"
              :router-to="handleRowClick(scope.row)"
            />
            <ActionIcon
              v-if="currentBuyingPageStatus === 'on_review' && reviewActions(scope.row)"
              icon="times"
              color="var(--text-negative)"
              :tooltip="$t('mediaBuying.actions.decline')"
              @click="declineHandler(scope.row)"
            />
            <ActionIcon
              v-if="currentBuyingPageStatus === 'processing' && reviewActions(scope.row)"
              icon="times"
              color="var(--text-negative)"
              :tooltip="$t('mediaBuying.actions.failed')"
              @click="failedHandler(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>
